import {
  HttpContextToken,
  HttpErrorResponse, HttpInterceptorFn, HttpRequest,
  HttpHandlerFn, HttpEvent,
} from '@angular/common/http';
import { catchError, throwError, BehaviorSubject, filter, take, switchMap, Observable } from 'rxjs';
import { inject } from '@angular/core';
import { AuthService, IS_REFRESH_TOKEN, IS_USER_PROFILE } from '../services/auth/auth.service';
import { Store } from '@ngrx/store';
import { AuthActions } from '../../store/auth/auth.actions';
import { UserAuthActions } from '../../store/user/auth/user-auth.actions';

// Marks a request that has already been retried after a token refresh — prevents infinite 401 loop
const IS_RETRIED = new HttpContextToken<boolean>(() => false);

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const store       = inject(Store);
  const accessToken  = authService.getAccessToken();
  const refreshToken = authService.getRefreshToken();

  const headers: Record<string, string> = {};

  if (req.context.get(IS_REFRESH_TOKEN)) {
    if (refreshToken) headers['Authorization'] = `Bearer ${refreshToken}`;
  } else if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  // Profile privileges decide which edit controls render. A cached GET can
  // answer one refresh with an older grant and the next with the live one.
  if (req.context.get(IS_USER_PROFILE)) {
    headers['Cache-Control'] = 'no-cache, no-store, max-age=0';
    headers['Pragma'] = 'no-cache';
  }

  const authReq = Object.keys(headers).length > 0 ? req.clone({ setHeaders: headers }) : req;

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      const isRefresh = req.context.get(IS_REFRESH_TOKEN);
      if (error.status === 401 && !isRefresh) {
        // Already retried once after refresh — don't loop, logout instead
        if (req.context.get(IS_RETRIED)) {
          store.dispatch(AuthActions.logout());
          return throwError(() => error);
        }

        if (!authService.getRefreshToken()) {
          store.dispatch(AuthActions.logout());
          return throwError(() => error);
        }

        return handle401Error(req, next, authService, store);
      }
      
      if (error.status === 403 && !isRefresh && !req.context.get(IS_USER_PROFILE)) {
        store.dispatch(UserAuthActions.loadUserDetails());
        return throwError(() => error);
      }

      // 401 or 403 on a refresh request — refresh itself is forbidden, logout
      if ((error.status === 401 || error.status === 403) && isRefresh) {
        store.dispatch(AuthActions.logout());
      }

      return throwError(() => error);
    })
  );
};

const handle401Error = (
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  store: Store
): Observable<HttpEvent<any>> => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    return authService.refreshToken().pipe(
      switchMap(response => {
        isRefreshing = false;
        const newToken = response.body?.data?.[0]?.accessToken;
        refreshTokenSubject.next(newToken || null);

        // Mark the retried request so a second 401 triggers logout, not another refresh
        const retried = request.clone({
          setHeaders: { Authorization: `Bearer ${newToken}` },
          context:    request.context.set(IS_RETRIED, true),
        });
        return next(retried);
      }),
      catchError(err => {
        isRefreshing = false;
        store.dispatch(AuthActions.logout());
        return throwError(() => err);
      })
    );
  } else {
    return refreshTokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => {
        const retried = request.clone({
          setHeaders: { Authorization: `Bearer ${token}` },
          context:    request.context.set(IS_RETRIED, true),
        });
        return next(retried);
      })
    );
  }
};
